import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { BarCode, Delete, Refresh, FileText, CloseSmall, PreviewOpen, Edit, Send, Redo, CheckOne, CloseOne, Upload } from '@icon-park/react';
import { useLabelList } from '../hooks/useLabelList';
import { getQRCodeBaseUrl, fetchLabelDetail, submitForReview, resubmitForReview, getLabelReviewStatus, publishLabel, refreshQRCode } from '../../../api/labels';
import { BUSINESS_CONFIG } from '../../../config/business.config';
import './LabelList.css';

// 标签状态配置
const LABEL_STATUS_CONFIG = {
  0: { text: '草稿', className: 'draft', canSubmit: true, canEdit: true },
  1: { text: '待AI审核', className: 'pending', canSubmit: false, canEdit: false },
  2: { text: 'AI审核中', className: 'processing', canSubmit: false, canEdit: false },
  3: { text: '待人工标注', className: 'pending', canSubmit: false, canEdit: false },
  4: { text: '人工标注中', className: 'processing', canSubmit: false, canEdit: false },
  5: { text: '待复核', className: 'pending', canSubmit: false, canEdit: false },
  6: { text: '复核中', className: 'processing', canSubmit: false, canEdit: false },
  7: { text: '已通过', className: 'approved', canSubmit: false, canEdit: false, canPublish: true },
  8: { text: '已驳回', className: 'rejected', canResubmit: true, canEdit: true },
  9: { text: '已发布', className: 'published', canSubmit: true, canEdit: true },
  10: { text: '已归档', className: 'archived', canSubmit: false, canEdit: false },
  11: { text: '已下架', className: 'offline', canSubmit: true, canEdit: true },
};

// 获取状态配置
const getStatusConfig = (status) => {
  // 处理字符串状态（兼容旧数据）
  if (typeof status === 'string') {
    if (status === 'published') return LABEL_STATUS_CONFIG[9];
    if (status === 'offline') return LABEL_STATUS_CONFIG[11];
    if (status === 'draft') return LABEL_STATUS_CONFIG[0];
    if (status === 'rejected') return LABEL_STATUS_CONFIG[8];
    if (status === 'approved') return LABEL_STATUS_CONFIG[7];
  }
  return LABEL_STATUS_CONFIG[status] || LABEL_STATUS_CONFIG[0];
};

/**
 * LabelList - 我的创建标签列表组件
 * 展示用户创建的所有标签，支持预览、编辑、查看二维码、删除等操作
 */
export function LabelList({ onCreateLabel, onEditLabel }) {
  const {
    labels,
    isLoading,
    error,
    pagination,
    handleDelete,
    changePage,
    refresh,
  } = useLabelList();

  // 二维码弹窗状态
  const [showQRCodeModal, setShowQRCodeModal] = useState(false);
  const [currentLabelId, setCurrentLabelId] = useState(null);
  const [currentLabelName, setCurrentLabelName] = useState('');
  const [currentLabelStatus, setCurrentLabelStatus] = useState(null);
  const [qrcodeBaseUrl, setQrcodeBaseUrl] = useState(null); // 从后端获取的二维码基础URL
  const [submittingId, setSubmittingId] = useState(null); // 正在提交审核的标签ID
  const [qrcodeCacheKey, setQrcodeCacheKey] = useState(() => Date.now());
  const [refreshingQRCode, setRefreshingQRCode] = useState(false);

  // 驳回详情弹窗状态
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectInfo, setRejectInfo] = useState(null);
  const [loadingRejectInfo, setLoadingRejectInfo] = useState(false);

  // 提交审核确认弹窗状态
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [submitConfirmData, setSubmitConfirmData] = useState(null);

  // 结果提示弹窗状态（成功/失败）
  const [showResultModal, setShowResultModal] = useState(false);
  const [resultModalData, setResultModalData] = useState(null); // { type: 'success'|'error', title, message }

  // 组件加载时从后端获取二维码基础URL
  useEffect(() => {
    const fetchQRCodeBaseUrl = async () => {
      try {
        const result = await getQRCodeBaseUrl();
        const baseUrl = result?.data || result || BUSINESS_CONFIG.QRCODE.BASE_URL || window.location.origin;
        setQrcodeBaseUrl(baseUrl);
      } catch (error) {
        console.warn('获取二维码基础URL失败，使用默认值:', error);
        setQrcodeBaseUrl(BUSINESS_CONFIG.QRCODE.BASE_URL || window.location.origin);
      }
    };

    fetchQRCodeBaseUrl();
  }, []);

  // 删除标签
  const handleDeleteClick = async (labelId) => {
    const success = await handleDelete(labelId);
    if (success) {
      // 可以显示成功提示
    }
  };

  // 生成标签访问URL
  const getLabelUrl = (labelId) => {
    const base = qrcodeBaseUrl || BUSINESS_CONFIG.QRCODE.BASE_URL || window.location.origin;
    return `${base}/view/${labelId}`;
  };

  // 处理预览
  const handlePreview = (labelId) => {
    const url = getLabelUrl(labelId);
    // 在新窗口打开预览
    window.open(url, '_blank');
  };

  // 处理编辑
  const handleEdit = async (labelId, status, labelName) => {
    if (!onEditLabel) {
      console.warn('onEditLabel回调未提供');
      return;
    }

    try {
      const isRevisionMode = [9, 11, 'published', 'offline'].includes(status);

      if (isRevisionMode) {
        const confirmed = window.confirm(
          `标签"${labelName}"当前处于已发布或已下架状态。\n继续后会直接在原标签上修订内容，保存后需重新提交审核；如需更新二维码，请在二维码弹窗中手动刷新。`
        );
        if (!confirmed) {
          return;
        }
      }

      const response = await fetchLabelDetail(labelId);
      const labelData = response?.data || response;

      if (labelData) {
        onEditLabel(labelData);
      } else {
        alert('获取标签详情失败，请稍后重试');
      }
    } catch (error) {
      console.error('获取标签详情失败:', error);
      alert('获取标签详情失败，请稍后重试');
    } finally {
      setSubmittingId(null);
    }
  };

  // 显示提交审核确认弹窗
  const handleSubmitReview = (labelId, labelName, isResubmit = false, status = null) => {
    setSubmitConfirmData({ labelId, labelName, isResubmit, status });
    setShowSubmitConfirm(true);
  };

  // 关闭提交审核确认弹窗
  const handleCloseSubmitConfirm = () => {
    setShowSubmitConfirm(false);
    setSubmitConfirmData(null);
  };

  // 显示结果弹窗
  const showResult = (type, title, message) => {
    setResultModalData({ type, title, message });
    setShowResultModal(true);
  };

  // 关闭结果弹窗
  const handleCloseResultModal = () => {
    setShowResultModal(false);
    setResultModalData(null);
  };

  // 确认提交审核
  const handleConfirmSubmit = async () => {
    if (!submitConfirmData) return;

    const { labelId, labelName, isResubmit, status } = submitConfirmData;
    const actionText = isResubmit ? '重新提交审核' : '提交审核';
    const isRevisionSubmit = [9, 11, 'published', 'offline'].includes(status);

    handleCloseSubmitConfirm();
    setSubmittingId(labelId);

    try {
      // 驳回后重新提交调用resubmit接口（跳过AI审核）
      if (isResubmit) {
        await resubmitForReview(labelId);
        showResult(
          'success',
          `${actionText}成功`,
          `标签"${labelName}"已成功提交，将直接进入人工复核阶段。`
        );
      } else {
        await submitForReview(labelId);
        showResult(
          'success',
          `${actionText}成功`,
          isRevisionSubmit
            ? `标签"${labelName}"的修订内容已成功提交，将重新进入AI审核流程，审核通过后继续使用原标签ID发布。`
            : `标签"${labelName}"已成功提交，将进入AI审核流程。`
        );
      }
      refresh();
    } catch (error) {
      console.error(`${actionText}失败:`, error);
      showResult(
        'error',
        `${actionText}失败`,
        error.message || '请稍后重试'
      );
    } finally {
      setSubmittingId(null);
    }
  };

  // 发布标签
  const handlePublish = async (label) => {
    const { id: labelId, labelName } = label;
    if (!window.confirm(`确定要发布标签"${labelName}"吗？\n发布后，标签将对所有人公开可见。`)) {
      return;
    }

    setSubmittingId(labelId);
    try {
      await publishLabel(labelId);
      showResult(
        'success',
        '发布成功',
        `标签"${labelName}"已成功发布，现在可以通过二维码或链接公开访问。`
      );
      refresh();
    } catch (error) {
      console.error('发布失败:', error);
      showResult(
        'error',
        '发布失败',
        error.message || '请稍后重试'
      );
    } finally {
      setSubmittingId(null);
    }
  };

  // 显示二维码弹窗
  const handleShowQRCode = (labelId, labelName, status) => {
    setCurrentLabelId(labelId);
    setCurrentLabelName(labelName);
    setCurrentLabelStatus(status);
    setQrcodeCacheKey(Date.now());
    setShowQRCodeModal(true);
  };

  // 关闭二维码弹窗
  const handleCloseQRCodeModal = () => {
    setShowQRCodeModal(false);
    setCurrentLabelId(null);
    setCurrentLabelName('');
    setCurrentLabelStatus(null);
    setRefreshingQRCode(false);
  };

  const canRefreshQRCode = [9, 11, 'published', 'offline'].includes(currentLabelStatus);
  const qrcodeImageSrc = currentLabelId
    ? `/api/foodlabel/labels/${currentLabelId}/qrcode?t=${qrcodeCacheKey}`
    : '';

  const handleRefreshQRCode = async () => {
    if (!currentLabelId) {
      return;
    }

    setRefreshingQRCode(true);
    try {
      await refreshQRCode(currentLabelId);
      const nextCacheKey = Date.now();
      setQrcodeCacheKey(nextCacheKey);
      refresh();
      showResult('success', '二维码已更新', `标签"${currentLabelName}"的二维码已刷新。`);
    } catch (error) {
      console.error('刷新二维码失败:', error);
      showResult('error', '刷新二维码失败', error.message || '请稍后重试');
    } finally {
      setRefreshingQRCode(false);
    }
  };

  // 显示驳回详情 - 从API获取审核任务详情
  const handleShowRejectInfo = async (label) => {
    const labelId = label.id || label.labelId;
    const labelName = label.labelName || label.name || '未命名标签';

    setLoadingRejectInfo(true);
    setShowRejectModal(true);
    setRejectInfo({
      labelName,
      rejectReason: '加载中...',
      reviewerName: null,
      reviewTime: null,
      taskId: null,
      hasIssues: false,
    });

    try {
      // 调用API获取当前审核任务详情
      const response = await getLabelReviewStatus(labelId);
      const taskData = response?.data || response;

      if (taskData) {
        // 检查是否有 AI 问题或人工标注
        const hasAiIssues = taskData.aiIssues && taskData.aiIssues.length > 2; // 非空JSON
        const hasAnnotations = taskData.annotations && taskData.annotations.length > 2;

        setRejectInfo({
          labelName,
          rejectReason: taskData.reviewOpinion || taskData.rejectReason || '暂无驳回原因说明',
          reviewerName: taskData.reviewerName,
          reviewTime: taskData.reviewTime,
          taskId: taskData.id || taskData.taskId,
          hasIssues: hasAiIssues || hasAnnotations,
          aiScore: taskData.aiScore,
        });
      } else {
        setRejectInfo({
          labelName,
          rejectReason: '无法获取驳回原因',
          reviewerName: null,
          reviewTime: null,
          taskId: null,
          hasIssues: false,
        });
      }
    } catch (error) {
      console.error('获取驳回详情失败:', error);
      setRejectInfo({
        labelName,
        rejectReason: '获取驳回原因失败，请稍后重试',
        reviewerName: null,
        reviewTime: null,
        taskId: null,
        hasIssues: false,
      });
    } finally {
      setLoadingRejectInfo(false);
    }
  };

  // 跳转到公开查看页面查看标注详情（无需登录）
  const handleViewAnnotationDetail = (taskId) => {
    // 仅允许数字ID，避免拼出 /task/annotation/view 导致后端转换异常
    if (!taskId || !/^\d+$/.test(String(taskId))) {
      alert('任务ID无效，无法查看详细标注');
      return;
    }
    // 新功能系统的公开查看页面URL（生产域名），无需登录
    const baseUrl = 'https://huapubio.com.cn';
    const url = `${baseUrl}/view/annotation?task=${taskId}`;
    window.open(url, '_blank');
  };

  // 关闭驳回详情弹窗
  const handleCloseRejectModal = () => {
    setShowRejectModal(false);
    setRejectInfo(null);
  };

  return (
    <div className="label-list-container">
      <div className="label-list-header">
        <h2 className="profile-title profile-title-plain">我的创建</h2>
        <div className="label-list-actions">
          <button 
            className="btn-refresh" 
            onClick={refresh}
            disabled={isLoading}
            title="刷新列表"
          >
            <Refresh theme="outline" size={16} />
            刷新
          </button>
          <button 
            className="btn-create-label" 
            onClick={onCreateLabel}
            title="创建新标签"
          >
            <FileText theme="outline" size={16} />
            创建标签
          </button>
        </div>
      </div>

      {error && (
        <div className="label-list-error">
          <p>{error}</p>
          <button className="btn-retry" onClick={refresh}>重试</button>
        </div>
      )}

      {isLoading && labels.length === 0 ? (
        <div className="label-list-loading">
          <p>正在加载标签列表...</p>
        </div>
      ) : labels.length === 0 ? (
        <div className="label-list-empty">
          <FileText theme="outline" size={48} fill="#94a3b8" />
          <p>您还没有创建任何标签</p>
          <button className="btn-create-label-primary" onClick={onCreateLabel}>
            立即创建
          </button>
        </div>
      ) : (
        <>
          <div className="label-list-content">
            <table className="label-list-table">
              <thead>
                <tr>
                  <th>标签名称</th>
                  <th>所属租户</th>
                  <th>所属企业</th>
                  <th>更新时间</th>
                  <th>状态</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {labels.map((label) => {
                  const labelId = label.id || label.labelId;
                  const labelName = label.labelName || label.name || '未命名标签';
                  const updateTime = label.updateTime || label.updatedAt || label.createTime || label.createdAt;
                  const status = label.status;
                  const statusConfig = getStatusConfig(status);
                  const isSubmitting = submittingId === labelId;
                  const isRejected = status === 8 || status === 'rejected';

                  return (
                    <tr key={labelId} className={isRejected ? 'row-rejected' : ''}>
                      <td className="label-name-cell">
                        <span className="label-name" title={labelName}>
                          {labelName}
                        </span>
                        {isRejected && (
                          <button
                            className="btn-reject-info"
                            onClick={() => handleShowRejectInfo(label)}
                            title="查看驳回原因"
                          >
                            查看原因
                          </button>
                        )}
                      </td>
                      <td className="label-tenant-cell">
                        <span className="label-tenant" title={label.tenantName || '-'}>
                          {label.tenantName || '-'}
                        </span>
                      </td>
                      <td className="label-enterprise-cell">
                        <span className="label-enterprise" title={label.enterpriseName || '-'}>
                          {label.enterpriseName || '-'}
                        </span>
                      </td>
                      <td>{updateTime || '-'}</td>
                      <td>
                        <span className={`status-tag status-${statusConfig.className}`}>
                          {statusConfig.text}
                        </span>
                      </td>
                      <td>
                        <div className="label-actions">
                          <button
                            className="btn-action btn-preview"
                            onClick={() => handlePreview(labelId)}
                            disabled={isLoading}
                            title="预览标签"
                          >
                            <PreviewOpen theme="outline" size={16} />
                            预览
                          </button>
                          <button
                            className="btn-action btn-edit"
                            onClick={() => handleEdit(labelId, status, labelName)}
                            disabled={isLoading || isSubmitting || !statusConfig.canEdit}
                            title={
                              statusConfig.canEdit
                                ? ([9, 11, 'published', 'offline'].includes(status) ? '进入原标签修订' : '编辑标签')
                                : '审核中不可编辑'
                            }
                          >
                            <Edit theme="outline" size={16} />
                            {[9, 11, 'published', 'offline'].includes(status) ? '修订' : '编辑'}
                          </button>
                          {/* 提交审核按钮（草稿、已发布、已下架状态） */}
                          {statusConfig.canSubmit && (
                            <button
                              className="btn-action btn-submit"
                              onClick={() => handleSubmitReview(labelId, labelName, false, status)}
                              disabled={isLoading || isSubmitting}
                              title="提交审核"
                            >
                              <Send theme="outline" size={16} />
                              {isSubmitting ? '提交中...' : ([9, 11, 'published', 'offline'].includes(status) ? '提交修订' : '提交审核')}
                            </button>
                          )}
                          {/* 重新提交审核按钮（已驳回状态） */}
                          {statusConfig.canResubmit && (
                            <button
                              className="btn-action btn-resubmit"
                              onClick={() => handleSubmitReview(labelId, labelName, true)}
                              disabled={isLoading || isSubmitting}
                              title="重新提交审核"
                            >
                              <Redo theme="outline" size={16} />
                              {isSubmitting ? '提交中...' : '重新提交'}
                            </button>
                          )}
                          {/* 发布按钮（已通过或已下架状态） */}
                          {statusConfig.canPublish && (
                            <button
                              className="btn-action btn-publish"
                              onClick={() => handlePublish(label)}
                              disabled={isLoading || isSubmitting}
                              title="发布标签"
                            >
                              <Upload theme="outline" size={16} />
                              {isSubmitting ? '发布中...' : '发布'}
                            </button>
                          )}
                          <button
                            className="btn-action btn-view-qrcode"
                            onClick={() => handleShowQRCode(labelId, labelName, status)}
                            title="查看二维码"
                          >
                            <BarCode theme="outline" size={16} />
                            查看二维码
                          </button>
                          <button
                            className="btn-action btn-delete"
                            onClick={() => handleDeleteClick(labelId)}
                            disabled={isLoading}
                            title="删除标签"
                          >
                            <Delete theme="outline" size={16} />
                            删除
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* 分页 */}
          {pagination.totalPages > 1 && (
            <div className="label-list-pagination">
              <button
                className="btn-page"
                onClick={() => changePage(pagination.pageNum - 1)}
                disabled={pagination.pageNum <= 1 || isLoading}
              >
                上一页
              </button>
              <span className="pagination-info">
                第 {pagination.pageNum} / {pagination.totalPages} 页
                {pagination.total > 0 && ` (共 ${pagination.total} 条)`}
              </span>
              <button
                className="btn-page"
                onClick={() => changePage(pagination.pageNum + 1)}
                disabled={pagination.pageNum >= pagination.totalPages || isLoading}
              >
                下一页
              </button>
            </div>
          )}
        </>
      )}

      {/* 二维码弹窗 - 使用 Portal 渲染到 body */}
      {showQRCodeModal && currentLabelId && ReactDOM.createPortal(
        <div className="qrcode-modal-overlay" onClick={handleCloseQRCodeModal}>
          <div className="qrcode-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="qrcode-modal-close" onClick={handleCloseQRCodeModal}>
              <CloseSmall theme="outline" size={18} />
            </button>
            <h2 className="qrcode-modal-title">标签二维码</h2>
            <p className="qrcode-modal-subtitle">{currentLabelName}</p>

            <div className="qrcode-modal-body">
              <div className="qrcode-display">
                <img
                  src={qrcodeImageSrc}
                  alt="标签二维码"
                  width={BUSINESS_CONFIG.QRCODE.DISPLAY_SIZE}
                  height={BUSINESS_CONFIG.QRCODE.DISPLAY_SIZE}
                  style={{ border: '1px solid #e8eaed', borderRadius: 8, background: '#fff' }}
                  onError={(e) => {
                    // 备用方案：使用外部API生成二维码
                    const size = BUSINESS_CONFIG.QRCODE.DISPLAY_SIZE;
                    const ecc = BUSINESS_CONFIG.QRCODE.ERROR_CORRECTION_LEVEL;
                    const shareLink = getLabelUrl(currentLabelId);
                    e.currentTarget.src = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&ecc=${ecc}&data=${encodeURIComponent(shareLink)}`;
                  }}
                />
              </div>
              <div className="qrcode-actions">
                <button
                  className="btn-qrcode-download"
                  onClick={async () => {
                    const url = `/api/foodlabel/labels/${currentLabelId}/qrcode`;
                    try {
                      const response = await fetch(url);
                      if (!response.ok) {
                        const text = await response.text();
                        console.error('[下载二维码] 错误响应:', text);
                        alert('下载失败: ' + response.statusText);
                        return;
                      }

                      const blob = await response.blob();

                      // 创建下载链接
                      const downloadUrl = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = downloadUrl;
                      a.download = `label-${currentLabelId}-qrcode.png`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(downloadUrl);
                    } catch (err) {
                      console.error('[下载二维码] 异常:', err);
                      alert('下载失败，请稍后重试');
                    }
                  }}
                >
                  下载二维码
                </button>
                  {canRefreshQRCode && (
                    <button
                      className="btn-qrcode-download"
                      onClick={handleRefreshQRCode}
                      disabled={refreshingQRCode}
                    >
                      {refreshingQRCode ? '更新中...' : '更新二维码'}
                    </button>
                  )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* 驳回详情弹窗 */}
      {showRejectModal && rejectInfo && ReactDOM.createPortal(
        <div className="reject-modal-overlay" onClick={handleCloseRejectModal}>
          <div className="reject-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="reject-modal-close" onClick={handleCloseRejectModal}>
              <CloseSmall theme="outline" size={18} />
            </button>
            <h2 className="reject-modal-title">驳回详情</h2>
            <p className="reject-modal-label">{rejectInfo.labelName}</p>
            <div className="reject-modal-body">
              {loadingRejectInfo ? (
                <p>正在加载驳回详情...</p>
              ) : (
                <>
                  <p className="reject-reason">{rejectInfo.rejectReason}</p>
                  {rejectInfo.reviewerName && (
                    <p className="reject-meta">审核人：{rejectInfo.reviewerName}</p>
                  )}
                  {rejectInfo.reviewTime && (
                    <p className="reject-meta">审核时间：{rejectInfo.reviewTime}</p>
                  )}
                  {rejectInfo.hasIssues && rejectInfo.taskId && (
                    <button
                      className="btn-view-annotation"
                      onClick={() => handleViewAnnotationDetail(rejectInfo.taskId)}
                    >
                      查看问题详情
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* 提交审核确认弹窗 */}
      {showSubmitConfirm && submitConfirmData && ReactDOM.createPortal(
        <div className="submit-confirm-overlay" onClick={handleCloseSubmitConfirm}>
          <div className="submit-confirm-content" onClick={(e) => e.stopPropagation()}>
            <button className="submit-confirm-close" onClick={handleCloseSubmitConfirm}>
              <CloseSmall theme="outline" size={18} />
            </button>

            <div className="submit-confirm-icon">
              <Send theme="outline" size={32} />
            </div>

            <h2 className="submit-confirm-title">
              {submitConfirmData.isResubmit ? '重新提交审核' : '提交审核'}
            </h2>

            <p className="submit-confirm-label-name">"{submitConfirmData.labelName}"</p>

            <p className="submit-confirm-desc">
              {submitConfirmData.isResubmit
                ? '重新提交后，标签将直接进入人工复核阶段（跳过AI审核）。'
                : ([9, 11, 'published', 'offline'].includes(submitConfirmData.status)
                  ? '提交后，当前标签的修订内容将重新进入AI审核，审核通过后继续以原标签ID发布。'
                  : '提交后，标签将进入AI审核流程，审核通过后方可发布使用。')
              }
            </p>

            <div className="submit-confirm-steps">
              {submitConfirmData.isResubmit ? (
                <>
                  <div className="step-item step-skip">
                    <span className="step-num">✓</span>
                    <span className="step-text">AI智能审核</span>
                    <span className="step-skip-tag">已完成</span>
                  </div>
                  <div className="step-arrow">→</div>
                  <div className="step-item step-active">
                    <span className="step-num">1</span>
                    <span className="step-text">人工复核</span>
                  </div>
                  <div className="step-arrow">→</div>
                  <div className="step-item">
                    <span className="step-num">2</span>
                    <span className="step-text">审核完成</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="step-item">
                    <span className="step-num">1</span>
                    <span className="step-text">AI智能审核</span>
                  </div>
                  <div className="step-arrow">→</div>
                  <div className="step-item">
                    <span className="step-num">2</span>
                    <span className="step-text">人工复核</span>
                  </div>
                  <div className="step-arrow">→</div>
                  <div className="step-item">
                    <span className="step-num">3</span>
                    <span className="step-text">审核完成</span>
                  </div>
                </>
              )}
            </div>

            <div className="submit-confirm-footer">
              <button className="btn-submit-cancel" onClick={handleCloseSubmitConfirm}>取消</button>
              <button className="btn-submit-confirm" onClick={handleConfirmSubmit}>
                <Send theme="outline" size={16} />
                确认提交
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* 结果提示弹窗（成功/失败） */}
      {showResultModal && resultModalData && ReactDOM.createPortal(
        <div className="result-modal-overlay" onClick={handleCloseResultModal}>
          <div className={`result-modal-content result-modal-${resultModalData.type}`} onClick={(e) => e.stopPropagation()}>
            <div className={`result-modal-icon result-icon-${resultModalData.type}`}>
              {resultModalData.type === 'success' ? (
                <CheckOne theme="filled" size={40} />
              ) : (
                <CloseOne theme="filled" size={40} />
              )}
            </div>
            <h2 className="result-modal-title">{resultModalData.title}</h2>
            <p className="result-modal-message">{resultModalData.message}</p>
            <div className="result-modal-footer">
              <button className="btn-result-close" onClick={handleCloseResultModal}>知道了</button>
            </div>
          </div>
        </div>,
        document.body
      )}

    </div>
  );
}

export default LabelList;
